import React, { useState, useEffect, useCallback, useRef } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../hooks/msalConfig';
import GetFetchGraph from '../helper/GetFetchGraph';

// En móviles los popups suelen bloquearse o romperse (webviews, in-app
// browsers), así que ahí usamos el flujo de redirect de MSAL en vez de popup.
const esMovil = () =>
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
        navigator.userAgent || ''
    );

// Bandeja de correo institucional. Solo funciona para quienes se autentican
// con Microsoft: la lectura del buzón requiere un token de Graph (Mail.Read).
//
// `correoEsperado` es el correo institucional del usuario que tiene sesión
// activa en APPESEN. La bandeja queda ANCLADA a ese correo: nunca usa una
// cuenta de Microsoft cacheada que pertenezca a otra persona (p. ej. en un
// equipo compartido), evitando que un usuario vea el buzón de otro.
const BandejaCorreo = ({ correoEsperado }) => {
    const [account, setAccount] = useState(null);
    const [mensajes, setMensajes] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);
    const [detalle, setDetalle] = useState(null);
    const [cargandoDetalle, setCargandoDetalle] = useState(false);
    const [alturaCorreo, setAlturaCorreo] = useState(null);
    const iframeRef = useRef(null);

    const esperado = (correoEsperado || '').trim().toLowerCase();

    // Busca, entre las cuentas Microsoft cacheadas, la que pertenece al usuario
    // logueado en APPESEN. Devuelve null si ninguna coincide.
    const cuentaDelUsuario = useCallback(() => {
        if (!esperado) return null;
        const cuentas = msalInstance.getAllAccounts();
        return cuentas.find((c) => (c.username || '').toLowerCase() === esperado) || null;
    }, [esperado]);

    // Token silencioso. Si caduca y se requiere interacción, solo re-autentica
    // cuando `permitirInteraccion` es true (gesto explícito del usuario): en
    // móvil con redirect, en desktop con popup. En la auto-carga NO se re-autentica
    // para evitar bucles de redirect al entrar a la página.
    const obtenerToken = useCallback(async (acc, permitirInteraccion = false) => {
        try {
            const resp = await msalInstance.acquireTokenSilent({ ...loginRequest, account: acc });
            return resp.accessToken;
        } catch (e) {
            if (e instanceof InteractionRequiredAuthError) {
                if (!permitirInteraccion) throw e;
                if (esMovil()) {
                    // Navega fuera de la app; el token se procesa al volver.
                    await msalInstance.acquireTokenRedirect({ ...loginRequest, account: acc, loginHint: esperado });
                    return null;
                }
                const resp = await msalInstance.acquireTokenPopup({ ...loginRequest, loginHint: esperado });
                return resp.accessToken;
            }
            throw e;
        }
    }, [esperado]);

    const cargarMensajes = useCallback(async (acc, permitirInteraccion = false) => {
        setCargando(true);
        setError(null);
        try {
            const token = await obtenerToken(acc, permitirInteraccion);
            if (!token) return; // en móvil hubo redirect: la página se recarga
            const data = await GetFetchGraph.getMensajes(token, 25);
            setMensajes(data.value || []);
        } catch (e) {
            console.log(e);
            setError('No se pudieron cargar los correos. Intenta reconectar tu cuenta.');
        } finally {
            setCargando(false);
        }
    }, [obtenerToken]);

    // Al entrar, solo auto-carga si hay una sesión Microsoft cacheada que
    // pertenezca AL MISMO usuario logueado en APPESEN.
    useEffect(() => {
        const propia = cuentaDelUsuario();
        if (propia) {
            setAccount(propia);
            cargarMensajes(propia);
        } else {
            setAccount(null);
            setMensajes([]);
        }
    }, [cuentaDelUsuario, cargarMensajes]);

    const conectar = async () => {
        setError(null);
        // login_hint precarga el correo del usuario; select_account evita
        // reusar en silencio una sesión Microsoft de otra persona.
        const req = { ...loginRequest, loginHint: esperado, prompt: 'select_account' };

        if (esMovil()) {
            // Redirect: navega fuera de la app y vuelve tras autenticarse. El
            // resultado se procesa en index.js (handleRedirectPromise) y, al
            // montar de nuevo, la auto-carga queda anclada al correo correcto
            // (si el usuario eligió otra cuenta MS, simplemente no coincide y
            // no se carga ningún buzón ajeno).
            try {
                await msalInstance.loginRedirect(req);
            } catch (e) {
                console.log(e);
                setError('No se pudo iniciar sesión con Microsoft.');
            }
            return;
        }

        try {
            const resp = await msalInstance.loginPopup(req);
            const usuarioMs = (resp.account.username || '').toLowerCase();
            if (esperado && usuarioMs !== esperado) {
                // Inició sesión con una cuenta Microsoft distinta a la suya.
                await msalInstance.logoutPopup({ account: resp.account }).catch(() => {});
                setAccount(null);
                setError(`Debes conectar con tu cuenta institucional (${correoEsperado}), no con otra cuenta de Microsoft.`);
                return;
            }
            setAccount(resp.account);
            cargarMensajes(resp.account);
        } catch (e) {
            console.log(e);
            setError('No se pudo conectar con tu cuenta de Microsoft.');
        }
    };

    // Abre el detalle del correo DENTRO de la app (sin redirigir a Outlook):
    // trae el cuerpo del mensaje desde Graph y lo muestra en un panel inline.
    const abrirDetalle = async (m) => {
        setCargandoDetalle(true);
        setError(null);
        setAlturaCorreo(null); // reinicia para no arrastrar la altura del correo anterior
        setDetalle({ id: m.id, cargando: true });
        try {
            const token = await obtenerToken(account, true);
            if (!token) return; // en móvil hubo redirect: la página se recarga
            const data = await GetFetchGraph.getMensaje(token, m.id);
            setDetalle(data);
        } catch (e) {
            console.log(e);
            setDetalle(null);
            setError('No se pudo abrir el correo. Intenta de nuevo.');
        } finally {
            setCargandoDetalle(false);
        }
    };

    const volver = () => setDetalle(null);

    const desconectar = async () => {
        const acc = account;
        setAccount(null);
        setMensajes([]);
        setDetalle(null);
        await msalInstance.logoutPopup({ account: acc }).catch(() => {});
    };

    // Documento aislado para el cuerpo del correo: iframe con sandbox SIN
    // scripts (no ejecuta JS del correo) y enlaces que abren en pestaña nueva.
    const srcDocCorreo = (body) => {
        const esHtml = body && body.contentType && body.contentType.toLowerCase() === 'html';
        const contenido = body && body.content ? body.content : '';
        const cuerpo = esHtml
            ? contenido
            : `<pre style="white-space:pre-wrap;word-wrap:break-word;font-family:inherit;">${contenido
                  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
        return `<!DOCTYPE html><html><head><meta charset="utf-8">`
            + `<base target="_blank">`
            + `<style>body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;background:#fff;margin:0;padding:16px;}`
            + `img{max-width:100%;height:auto;}a{color:#0b5ed7;}</style></head>`
            + `<body>${cuerpo}</body></html>`;
    };

    // Ajusta la altura del iframe al alto real del correo. Solo funciona porque
    // el sandbox incluye `allow-same-origin` (sin `allow-scripts`): podemos leer
    // el documento, pero el JS del correo sigue sin ejecutarse.
    const ajustarAltura = () => {
        try {
            const doc = iframeRef.current.contentWindow.document;
            const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
            if (h) setAlturaCorreo(h + 16);
        } catch (e) {
            // Si el navegador no deja leer el documento, se queda con la altura fija.
        }
    };

    const formatoFecha = (iso) => {
        try {
            return new Date(iso).toLocaleString('es-SV', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
            });
        } catch (e) {
            return iso;
        }
    };

    // Sin sesión Microsoft propia: la bandeja queda deshabilitada. Solo se
    // habilita iniciando sesión con las credenciales de Microsoft (que además
    // garantiza que el buzón mostrado es el del usuario logueado).
    if (!account) {
        return (
            <div className="card mt-4">
                <div className="card-body text-center py-5">
                    <p className="display-4 text-primary mb-3">
                        <i className="fa fa-lock" aria-hidden="true"></i>
                    </p>
                    <h3 className="h5 mb-2">Ver tu correo institucional</h3>
                    <p className="text-muted mb-4">
                        Para ver tu correo dentro de la app debes iniciar sesión con tus
                        credenciales de Microsoft{esperado ? ` (${correoEsperado})` : ''}.
                        Mientras no inicies sesión con Microsoft, esta opción permanece deshabilitada.
                    </p>
                    {error ? (
                        <div className="alert alert-danger mb-0">{error}</div>
                    ) : (
                        <button className="btn btn-primary" onClick={conectar}>
                            <i className="fa fa-sign-in mr-2" aria-hidden="true"></i>
                            Iniciar sesión con Microsoft
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Vista de detalle del correo, dentro de la app.
    if (detalle) {
        const remitente = detalle.from && detalle.from.emailAddress ? detalle.from.emailAddress : null;
        return (
            <div className="mt-4">
                <button className="btn btn-sm btn-outline-secondary mb-3" onClick={volver}>
                    <i className="fa fa-arrow-left mr-1" aria-hidden="true"></i>
                    Volver a la bandeja
                </button>

                {error ? <div className="alert alert-danger">{error}</div> : null}

                {cargandoDetalle || detalle.cargando ? (
                    <div className="text-center py-5 text-muted">
                        <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        Abriendo correo…
                    </div>
                ) : (
                    <div className="card">
                        <div className="card-body">
                            <h3 className="h5 mb-2">{detalle.subject || '(sin asunto)'}</h3>
                            <p className="text-muted mb-1">
                                <strong>{remitente ? remitente.name : 'Desconocido'}</strong>
                                {remitente && remitente.address ? ` <${remitente.address}>` : ''}
                            </p>
                            <p className="text-muted small mb-3">{formatoFecha(detalle.receivedDateTime)}</p>
                            <iframe
                                ref={iframeRef}
                                title="Contenido del correo"
                                onLoad={ajustarAltura}
                                sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                                srcDoc={srcDocCorreo(detalle.body)}
                                style={{ width: '100%', height: alturaCorreo ? `${alturaCorreo}px` : '60vh', border: '1px solid rgba(0,0,0,.1)', borderRadius: '.25rem', background: '#fff' }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="h5 mb-0">Bandeja de entrada</h3>
                <div>
                    <button
                        className="btn btn-sm btn-outline-primary mr-2"
                        onClick={() => cargarMensajes(account, true)}
                        disabled={cargando}
                    >
                        <i className="fa fa-refresh mr-1" aria-hidden="true"></i>
                        Actualizar
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={desconectar}>
                        <i className="fa fa-sign-out mr-1" aria-hidden="true"></i>
                        Desconectar
                    </button>
                </div>
            </div>

            {error ? <div className="alert alert-danger">{error}</div> : null}

            {cargando ? (
                <div className="text-center py-5 text-muted">
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    Cargando correos…
                </div>
            ) : mensajes.length === 0 ? (
                <div className="card">
                    <div className="card-body text-center text-muted py-5">
                        No hay correos para mostrar.
                    </div>
                </div>
            ) : (
                <div className="list-group">
                    {mensajes.map((m) => (
                        <button
                            key={m.id || m.webLink}
                            type="button"
                            onClick={() => abrirDetalle(m)}
                            className={`list-group-item list-group-item-action text-left ${m.isRead ? '' : 'font-weight-bold'}`}
                            style={!m.isRead ? { borderLeft: '3px solid #0d6efd' } : undefined}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-truncate mr-2">
                                    {!m.isRead ? (
                                        <span
                                            className="d-inline-block mr-2"
                                            title="No leído"
                                            style={{ width: 8, height: 8, borderRadius: '50%', background: '#0d6efd' }}
                                        />
                                    ) : null}
                                    {m.from && m.from.emailAddress ? m.from.emailAddress.name : 'Desconocido'}
                                </span>
                                <small className="text-muted text-nowrap">{formatoFecha(m.receivedDateTime)}</small>
                            </div>
                            <div className="text-truncate">{m.subject || '(sin asunto)'}</div>
                            <small className="text-muted d-block text-truncate">{m.bodyPreview}</small>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BandejaCorreo;
